/**
 * What the marketing site receives from the CMS delivery API.
 *
 * Deliberately thin. The delivery routes return the **published snapshot** and nothing else — no
 * working copy, no lifecycle, no editing metadata — and these types say so by having nowhere to
 * put any of it. A shape that could carry a draft is a shape somebody eventually fills.
 *
 * `CMS-012` adds the one exception, and keeps it a separate type rather than a flag on this one —
 * see {@link PreviewContent}.
 */

/** Opaque per-type content. Its keys are the content type's field keys. */
export type ContentData = Readonly<Record<string, unknown>>;

export interface PublishedContent {
  readonly slug: string;
  readonly publishedAt: string;
  readonly data: ContentData;
}

/**
 * What a valid preview token resolves to (`CMS-012` AC-1) — a **draft**.
 *
 * A distinct type from {@link PublishedContent} rather than an `isPreview?: boolean` on it, and
 * the distinctness is the safeguard rather than a stylistic preference. The two are not mutually
 * assignable: `PreviewContent` has no `publishedAt` and `PublishedContent` has no `isPreview`, so
 * a component that renders one cannot be handed the other by accident. An optional flag would
 * have made "a draft" and "a published page" the same shape with a field that defaults to
 * *false*, which is to say defaults to claiming a draft is live.
 *
 * `isPreview` is the literal `true`, so this cannot be constructed as anything else.
 */
export interface PreviewContent {
  readonly typeKey: string;
  readonly slug: string;
  readonly title: string;
  readonly data: ContentData;
  readonly isPreview: true;
  readonly previewExpiresAt: string;
}

/** A section of a page: its kind in `_type`, and that kind's own fields beside it. */
export type PageSection = ContentData & { readonly _type?: string };

export const SECTION_TYPE_KEY = '_type';

export function sectionType(section: PageSection): string {
  const value = section[SECTION_TYPE_KEY];
  return typeof value === 'string' ? value : '';
}

/** The sections of a page, or an empty list when it has none or the field is the wrong shape. */
export function pageSections(data: ContentData): ReadonlyArray<PageSection> {
  const sections = data['sections'];
  return Array.isArray(sections) ? (sections as ReadonlyArray<PageSection>) : [];
}

/** A string field, or `''`. Used everywhere a section reads its own copy. */
export function text(data: ContentData, key: string): string {
  const value = data[key];
  return typeof value === 'string' ? value : '';
}

/** A list field, or an empty list. */
export function list(data: ContentData, key: string): ReadonlyArray<ContentData> {
  const value = data[key];
  return Array.isArray(value) ? (value as ReadonlyArray<ContentData>) : [];
}
